// TanStack Query hook for the comms channel catalogue.
// Stays cached across page mounts so navigating away and back doesn't refetch.

import { useQuery } from '@tanstack/react-query';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import { commsKeys } from '../queryKeys.js';

export interface IUseChannelsOptions {
  /**
   * A page that owns the list fetches it when it mounts (the default). Screens
   * that mount inside such a page (mobile's stack) pass `false`: they read the
   * cached list, so opening a conversation doesn't refetch it — a refetch
   * answered before a message arrived would otherwise land after it and roll
   * the preview back.
   */
  refetchOnMount?: boolean;
}

export function useChannels(siteId: string | null, { refetchOnMount }: IUseChannelsOptions = {}) {
  const { fetchChannels } = useClientApis().comms;
  return useQuery({
    queryKey: commsKeys.channels(siteId ?? ''),
    queryFn: ({ signal }) => fetchChannels(siteId!, signal),
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: refetchOnMount ?? 'always',
    refetchOnWindowFocus: true,
  });
}
